import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@primereact/ui/button';

import { EsDialog } from './EsPrime';
import { useEsStrings } from './EsStrings';

/*
 * Decorative MZ4250 miniatures behind a sparse page, the way EsHomeHero stands its wizard
 * beside the pitch (Tim, 2026-10-27): a few clay renders at the edges and faintly behind the
 * middle, a different set and arrangement on every page load.
 *
 * WHERE THE FILES ARE. Not in the bundle: 45 PNGs are 12 MB, and the home hero's one import
 * is already the largest image webpack ships. They are plain URLs under /media/minis/<name>.webp
 * on the page's own origin -- CloudFront's /media/* behavior over S3 once the site sits behind
 * it, and until then esfront's nginx, whose image builds the same WebP files from
 * src/images/minis (Dockerfile, cmd/miniwebp.sh); the dev server serves target/minis there
 * (webpack.config.js devServer.static). Each file is 520 px wide, 16-31 KB (23 on average),
 * cached immutable.
 *
 * WHAT A PAGE COSTS. None below md, one at md (768), two at lg (1024), three from xl (1280):
 * 0, ~23, ~45, ~68 KB, and two more (~45 KB) where `beside` pins them, at any of those widths;
 * an edge mini a pin covers is still fetched. The count follows matchMedia rather than a hidden class, so a phone
 * fetches nothing. No matchMedia (jest, a server render) is no minis at all.
 *
 * DECORATIVE TO A READER. alt="" and aria-hidden, z-index -1 inside the page's own stacking
 * context (every page section is `relative z-10`; EsHome isolates its wrapper for
 * it), absolute so nothing shifts, and faint: half opacity at an edge, where nothing reads over
 * it, and a tenth behind the column, which the house text colors clear in both modes. A load
 * error hides that slot, so a missing upload costs nothing visible.
 *
 * A CLICK OPENS AN EDGE MINI (Tim, 2026-10-29; reworked 2026-11-02). A button in the faint
 * layer was never reached: the sections stand over it at z-10, and they span the page. So the
 * figures stay below and an edge mini's button sits in a second layer ABOVE the sections
 * (z-20), pointer-events none until the pointer is over a part of it that is in the clear.
 * Geometry cannot say where that is: at 820 and 1180 an edge mini reaches into a 640 or 760
 * column, and EsHome's sections are full width, some opaque. So `isClear` asks the browser
 * what is under the point: clear only when it is inside the page and it, and everything up to
 * the page, is inert -- no control, no text of its own, no picture, no background. Only then
 * does the button take the pointer, and a scroll re-asks, so the page's own controls keep every
 * click they had. Touch has no hover: a tap on an inert point over an edge mini opens it from
 * the page's click instead, after the page has had it. A mini behind the column (a tenth
 * opacity) takes no click at all. The popup (EsMiniView) steps through every render, not only
 * the page's three. Each mini is a button out of the tab order, as both layers are out of the
 * accessibility tree: the figures carry nothing a reader needs.
 *
 * PINNED BESIDE A COLUMN (Tim, 2026-11-02: "minis to the left and right of roadmap"). `beside`
 * names a narrow column inside the page, by selector; the same layers then carry two more minis,
 * one in the gutter at each side of it, beside it rather than behind it. They are this
 * instance's and not a second EsMinis inside the column, because one seed is what keeps a mini
 * from showing twice and one layer is what keeps a random edge mini off a pinned one: an edge
 * mini whose box meets a pin goes invisible (it keeps its box, so a resize can bring it back) and
 * loses its button. Where they sit is measured, in px, on mount and on every resize of the page:
 * a CSS calc on 100vw counts the scrollbar and runs past the page. Each fills its gutter less a
 * 16 px gap at both sides, up to its drawn width, and is not drawn narrower than 64 px. Below md
 * there are none, as for the rest. On EsHome's 570 px roadmap that is ~93 px at 820 and the drawn
 * 150-240 px from 1180 up. They take a click the way an edge mini does.
 *
 * ONE SEED PER MOUNT. The arrangement is drawn from a seed taken once in useState, so a
 * re-render repeats the same picks and nothing flickers; the next page load draws again.
 * pickMinis and pinMinis are pure over the seed, which is what EsMinis.test.jsx asserts.
 */

export const MINIS_PATH = '/media/minis/';

/** The renders in src/images/minis, by file name. EsMinis.test.jsx keeps this list and that directory equal. */
export const MINI_NAMES = [
    'miniBugbear',
    'miniDireWolf',
    'miniDragonbornFemalePaladin',
    'miniDragonbornFemaleWizard',
    'miniDwarfFighter',
    'miniDwarfMaleCleric',
    'miniDwarfWizard',
    'miniElfFemaleBard',
    'miniElfFemaleCleric',
    'miniElfFemaleFighter',
    'miniElfFemaleWizard',
    'miniElfMaleDruid',
    'miniElfMaleRanger',
    'miniElfMaleSorcerer',
    'miniGnomeFemaleWizard',
    'miniGoblinArcher',
    'miniGoblinBoss',
    'miniGoblinWarrior',
    'miniHalflingFemaleBard',
    'miniHalflingFemaleRogue',
    'miniHalfOrcMaleBarbarian',
    'miniHalfOrcMaleRanger',
    'miniHillGiant',
    'miniHumanFemaleBarbarian',
    'miniHumanFemaleDruid',
    'miniHumanFemaleFighter',
    'miniHumanFemaleRanger',
    'miniHumanFemaleSorcerer',
    'miniHumanFemaleWarlock',
    'miniHumanMaleCleric',
    'miniHumanMaleFighter',
    'miniHumanMaleMonk',
    'miniHumanMalePaladin',
    'miniHumanMaleRogue',
    'miniHumanMaleWizard',
    'miniOwlbear',
    'miniRedDragon',
    'miniSkeleton',
    'miniSkeletonArcher',
    'miniTieflingFemaleMonk',
    'miniTieflingFemaleRogue',
    'miniTieflingMaleWarlock',
    'miniWolf',
    'miniYoungGreenDragon',
    'miniZombie',
];

// The renders' aspect, 620 x 778 (cmd/minirender.py).
const ASPECT = 778 / 620;
const MIN_WIDTH = 150;
const MAX_WIDTH = 240;
const SIDES = [ 'right', 'left', 'center' ];

// Tailwind 4's md, lg, and xl, the widths the house tests at (test/esWidths.js).
const QUERIES = [ '(min-width: 768px)', '(min-width: 1024px)', '(min-width: 1280px)' ];

export const miniUrl = ( pName ) => MINIS_PATH + pName + '.webp';

/** MZ4250's designs on Thingiverse: a mini's link where its own thing is not known. */
export const MINI_THINGIVERSE = 'https://www.thingiverse.com/mz4250/designs';

/**
 * Each render's thing on Thingiverse, by mz4250: the model's own page, or the collection that
 * carries it, at the version cmd/minirender.py drew (minis.json's stl path says which). Young
 * Green Dragon is missing: its "Green Dragon Updated" thing number could not be found (2026-10-30).
 */
const MINI_THINGS = {
    miniBugbear:                 'https://www.thingiverse.com/thing:3342329',
    miniDireWolf:                'https://www.thingiverse.com/thing:4982755',
    miniDragonbornFemalePaladin: 'https://www.thingiverse.com/thing:2434880',
    miniDragonbornFemaleWizard:  'https://www.thingiverse.com/thing:2435009',
    miniDwarfFighter:            'https://www.thingiverse.com/thing:2434991',
    miniDwarfMaleCleric:         'https://www.thingiverse.com/thing:2434880',
    miniDwarfWizard:             'https://www.thingiverse.com/thing:2435009',
    miniElfFemaleBard:           'https://www.thingiverse.com/thing:2434314',
    miniElfFemaleCleric:         'https://www.thingiverse.com/thing:2434880',
    miniElfFemaleFighter:        'https://www.thingiverse.com/thing:2434991',
    miniElfFemaleWizard:         'https://www.thingiverse.com/thing:2435009',
    miniElfMaleDruid:            'https://www.thingiverse.com/thing:2435009',
    miniElfMaleRanger:           'https://www.thingiverse.com/thing:2435041',
    miniElfMaleSorcerer:         'https://www.thingiverse.com/thing:2435009',
    miniGnomeFemaleWizard:       'https://www.thingiverse.com/thing:2435009',
    miniGoblinArcher:            'https://www.thingiverse.com/thing:3463399',
    miniGoblinBoss:              'https://www.thingiverse.com/thing:3463399',
    miniGoblinWarrior:           'https://www.thingiverse.com/thing:3463399',
    miniHalflingFemaleBard:      'https://www.thingiverse.com/thing:2434314',
    miniHalflingFemaleRogue:     'https://www.thingiverse.com/thing:2435041',
    miniHalfOrcMaleBarbarian:    'https://www.thingiverse.com/thing:2434108',
    miniHalfOrcMaleRanger:       'https://www.thingiverse.com/thing:2435041',
    miniHillGiant:               'https://www.thingiverse.com/thing:3930384',
    miniHumanFemaleBarbarian:    'https://www.thingiverse.com/thing:2434108',
    miniHumanFemaleDruid:        'https://www.thingiverse.com/thing:2435009',
    miniHumanFemaleFighter:      'https://www.thingiverse.com/thing:2434991',
    miniHumanFemaleRanger:       'https://www.thingiverse.com/thing:2435041',
    miniHumanFemaleSorcerer:     'https://www.thingiverse.com/thing:2435009',
    miniHumanFemaleWarlock:      'https://www.thingiverse.com/thing:2435009',
    miniHumanMaleCleric:         'https://www.thingiverse.com/thing:2434880',
    miniHumanMaleFighter:        'https://www.thingiverse.com/thing:2434991',
    miniHumanMaleMonk:           'https://www.thingiverse.com/thing:2435021',
    miniHumanMalePaladin:        'https://www.thingiverse.com/thing:2434880',
    miniHumanMaleRogue:          'https://www.thingiverse.com/thing:2435041',
    miniHumanMaleWizard:         'https://www.thingiverse.com/thing:2435009',
    miniOwlbear:                 'https://www.thingiverse.com/thing:2844099',
    miniRedDragon:               'https://www.thingiverse.com/thing:4605980',
    miniSkeleton:                'https://www.thingiverse.com/thing:4920002',
    miniSkeletonArcher:          'https://www.thingiverse.com/thing:4920002',
    miniTieflingFemaleMonk:      'https://www.thingiverse.com/thing:2435021',
    miniTieflingFemaleRogue:     'https://www.thingiverse.com/thing:2435041',
    miniTieflingMaleWarlock:     'https://www.thingiverse.com/thing:2435009',
    miniWolf:                    'https://www.thingiverse.com/thing:4982755',
    miniZombie:                  'https://www.thingiverse.com/thing:2847691',
};

/** The Thingiverse page for pName: its thing, or MZ4250's designs. */
export const miniThing = ( pName ) => MINI_THINGS[ pName ] ?? MINI_THINGIVERSE;

/** 'miniHalfOrcMaleBarbarian' as 'Half Orc Male Barbarian'. A model's name, the same in every language. */
export const miniTitle = ( pName ) => pName.replace( /^mini/, '' ).replace( /([a-z])([A-Z])/g, '$1 $2' );

/** The mini pStep places after pName in MINI_NAMES, wrapping at either end. */
export const miniStep = ( pName, pStep ) => {
    const aAt = Math.max( 0, MINI_NAMES.indexOf( pName ) );
    const aCount = MINI_NAMES.length;
    return MINI_NAMES[ ( ( aAt + pStep ) % aCount + aCount ) % aCount ];
};

/** How many minis a window pWidth wide shows: none below md, then one more at each of md, lg, and xl. */
export function miniCountFor( pWidth ) {
    return pWidth >= 1280 ? 3 : pWidth >= 1024 ? 2 : pWidth >= 768 ? 1 : 0;
}

// mulberry32: a small seeded generator, so one seed is one arrangement.
function random( pSeed ) {
    let aState = pSeed >>> 0;
    return () => {
        aState = ( aState + 0x6D2B79F5 ) >>> 0;
        let aValue = aState;
        aValue = Math.imul( aValue ^ ( aValue >>> 15 ), aValue | 1 );
        aValue ^= aValue + Math.imul( aValue ^ ( aValue >>> 7 ), aValue | 61 );
        return ( ( aValue ^ ( aValue >>> 14 ) ) >>> 0 ) / 4294967296;
    };
}

function shuffled( pList, pNext ) {
    const aList = [ ...pList ];
    for ( let aIx = aList.length - 1; aIx > 0; aIx-- ) {
        const aSwap = Math.floor( pNext() * ( aIx + 1 ) );
        [ aList[ aIx ], aList[ aSwap ] ] = [ aList[ aSwap ], aList[ aIx ] ];
    }
    return aList;
}

/**
 * pCount minis for one page load, never the same mini twice: each with a side (right, left,
 * or center, every side once before any repeats), a top as a percentage of the page between
 * pStart and pEnd (the page split into pCount bands, one mini in each, so they never stack),
 * a width between 150 and 240 px, an inset from its edge, and whether it faces the other way.
 * pExclude keeps a mini the page already shows (the hero's wizard) out of the draw.
 */
export function pickMinis( pSeed, pCount, { exclude = [], start = 8, end = 78 } = {} ) {
    const aNext = random( pSeed );
    const aNames = shuffled( MINI_NAMES.filter( ( pName ) => !exclude.includes( pName ) ), aNext );
    const aSides = shuffled( SIDES, aNext );
    const aCount = Math.min( pCount, aNames.length );
    const aBand = ( end - start ) / Math.max( aCount, 1 );
    const aPicks = [];
    for ( let aIx = 0; aIx < aCount; aIx++ ) {
        const aWidth = Math.round( MIN_WIDTH + aNext() * ( MAX_WIDTH - MIN_WIDTH ) );
        aPicks.push( {
            name: aNames[ aIx ],
            side: aSides[ aIx % SIDES.length ],
            top: Math.round( start + aBand * aIx + aNext() * aBand * 0.6 ),
            width: aWidth,
            height: Math.round( aWidth * ASPECT ),
            inset: Math.round( 2 + aNext() * 6 ),
            flip: aNext() < 0.5,
        } );
    }
    return aPicks;
}

const PIN_GAP = 16;
const PIN_FLOOR = 64;

/**
 * The two minis pinned beside a column: one left and one right, in either order, drawn from the
 * END of the same shuffle pickMinis draws from the front of, so neither repeats an edge mini at
 * any count. Each carries its drawn width and height, `drop` (how far down the column's free
 * height it stands, 0 to 0.6), and whether it faces the other way.
 */
export function pinMinis( pSeed, { exclude = [] } = {} ) {
    const aNames = shuffled( MINI_NAMES.filter( ( pName ) => !exclude.includes( pName ) ), random( pSeed ) );
    const aNext = random( ( pSeed ^ 0x5bd1e995 ) >>> 0 );
    const aSides = aNext() < 0.5 ? [ 'left', 'right' ] : [ 'right', 'left' ];
    return aSides.map( ( pSide, aIx ) => {
        const aWidth = Math.round( MIN_WIDTH + aNext() * ( MAX_WIDTH - MIN_WIDTH ) );
        return {
            name: aNames[ aNames.length - 1 - aIx ],
            side: pSide,
            width: aWidth,
            height: Math.round( aWidth * ASPECT ),
            drop: aNext() * 0.6,
            flip: aNext() < 0.5,
        };
    } );
}

/**
 * Where pPin stands beside pColumn ( { left, top, width, height }, px in the page ) on a page
 * pWidth wide: in its gutter, PIN_GAP off the column and off the page's edge, as wide as that
 * leaves up to its drawn width, and down the column by its drop. Null when the gutter leaves
 * less than PIN_FLOOR.
 */
export function pinPlace( pPin, pColumn, pWidth ) {
    const aRight = pColumn.left + pColumn.width;
    const aGutter = pPin.side === 'left' ? pColumn.left : pWidth - aRight;
    const aWidth = Math.min( pPin.width, Math.floor( aGutter - 2 * PIN_GAP ) );
    if ( aWidth < PIN_FLOOR ) return null;
    const aHeight = Math.round( aWidth * ASPECT );
    return {
        left: pPin.side === 'left' ? pColumn.left - PIN_GAP - aWidth : aRight + PIN_GAP,
        top: Math.round( pColumn.top + Math.max( 0, pColumn.height - aHeight ) * pPin.drop ),
        width: aWidth,
        height: aHeight,
    };
}

// An element's layout box inside pRoot, from offsets: a transform (EsHome's fadeInUp) moves
// what getBoundingClientRect reports, and would put a pin where the column was mid-animation.
function boxIn( pEl, pRoot ) {
    let aLeft = 0;
    let aTop = 0;
    for ( let aEl = pEl; aEl && aEl !== pRoot; aEl = aEl.offsetParent ) {
        aLeft += aEl.offsetLeft;
        aTop += aEl.offsetTop;
    }
    return { left: aLeft, top: aTop, width: pEl.offsetWidth, height: pEl.offsetHeight };
}

const meets = ( pA, pB ) => pA.left < pB.left + pB.width && pB.left < pA.left + pA.width
    && pA.top < pB.top + pB.height && pB.top < pA.top + pA.height;

function countNow() {
    if ( typeof window === 'undefined' || typeof window.matchMedia !== 'function' ) return 0;
    return QUERIES.filter( ( pQuery ) => window.matchMedia( pQuery ).matches ).length;
}

/** miniCountFor the window, live across a resize. */
export function useEsMiniCount() {
    const [ count, setCount ] = useState( countNow );
    useEffect( () => {
        if ( typeof window.matchMedia !== 'function' ) return undefined;
        const aLists = QUERIES.map( ( pQuery ) => window.matchMedia( pQuery ) );
        const update = () => setCount( countNow() );
        aLists.forEach( ( pList ) => pList.addEventListener( 'change', update ) );
        return () => aLists.forEach( ( pList ) => pList.removeEventListener( 'change', update ) );
    }, [] );
    return count;
}

const EDGE = 'opacity-50 dark:opacity-40';
const CENTER = 'opacity-[0.12] dark:opacity-[0.10]';

function placement( pPick ) {
    // Never lower than its own height above the page's bottom: on a short page the band's
    // percentage put the figure past the layer's overflow-hidden edge and cut its feet off.
    const aStyle = { top: 'max(0px, min(' + pPick.top + '%, calc(100% - ' + pPick.height + 'px)))',
        width: pPick.width + 'px', height: pPick.height + 'px' };
    const aFlip = pPick.flip ? ' scaleX(-1)' : '';
    if ( pPick.side === 'center' ) {
        aStyle.left = '50%';
        aStyle.transform = 'translateX(-50%)' + aFlip;
    }
    else {
        aStyle[ pPick.side ] = pPick.inset + '%';
        if ( aFlip ) aStyle.transform = aFlip.trim();
    }
    return aStyle;
}

// What makes a point busy: a control, a picture, or anything carrying a role or a tab stop.
const BUSY = 'a, button, input, select, textarea, label, summary, img, svg, video, canvas, iframe, [role], [tabindex], [contenteditable]';
// A computed background color with no alpha left; empty is jsdom's, which computes none.
const TRANSPARENT = /^$|^transparent$|^rgba\(.*,\s*0\)$|\/\s*0\)$/;

const ownText = ( pEl ) => Array.from( pEl.childNodes )
    .some( ( pNode ) => pNode.nodeType === Node.TEXT_NODE && pNode.textContent.trim() !== '' );

/**
 * Whether the point ( pX, pY ) shows the page's background there, so a mini drawn under it is in
 * the clear: the topmost element under it, past the minis' own layers, is inside pRoot, and it
 * and every element up to pRoot is inert (see the header). No hit testing (jsdom) is no veto.
 */
export function isClear( pRoot, pX, pY ) {
    if ( !pRoot ) return false;
    if ( typeof document.elementsFromPoint !== 'function' ) return true;
    const aHit = document.elementsFromPoint( pX, pY ).find( ( pEl ) => !pEl.closest( '[data-es-minis]' ) );
    if ( !aHit || !pRoot.contains( aHit ) ) return false;
    for ( let aEl = aHit; aEl && aEl !== pRoot; aEl = aEl.parentElement ) {
        if ( aEl.matches( BUSY ) || ownText( aEl ) ) return false;
        const aStyle = window.getComputedStyle( aEl );
        if ( aStyle.cursor === 'pointer' || ( aStyle.backgroundImage || 'none' ) !== 'none'
            || !TRANSPARENT.test( aStyle.backgroundColor ) ) return false;
    }
    return true;
}

/**
 * The layers. Inside an element that is `relative` and makes a stacking context (`z-10` or
 * `isolate`); the figures fill that element and draw behind its content, and the edge minis'
 * buttons fill it again above its sections. `beside`, a selector inside that element, pins two
 * more beside the column it names (see the header).
 */
export default function EsMinis( { exclude = [], start = 8, end = 78, beside, 'data-testid': testId = 'es-minis' } ) {
    const [ seed ] = useState( () => Math.floor( Math.random() * 0x7fffffff ) );
    const [ failed, setFailed ] = useState( () => new Set() );
    const [ viewing, setViewing ] = useState( null );
    const [ live, setLive ] = useState( null );
    const [ pinned, setPinned ] = useState( {} );
    const [ covered, setCovered ] = useState( () => new Set() );
    const figures = useRef( null );
    const targets = useRef( null );
    const aCount = useEsMiniCount();

    // Where the pins stand and which edge minis they cover, measured again whenever the page
    // or its column changes size. No column (or no layout, as in jsdom) is no pins.
    useEffect( () => {
        const aRoot = figures.current?.parentElement;
        if ( !aRoot || !beside ) return undefined;
        const aPins = pinMinis( seed, { exclude } );
        const measure = () => {
            const aColumn = aRoot.querySelector( beside );
            const aNext = {};
            if ( aColumn && aColumn.offsetWidth > 0 ) {
                const aBox = boxIn( aColumn, aRoot );
                aPins.forEach( ( pPin ) => {
                    const aAt = pinPlace( pPin, aBox, aRoot.clientWidth );
                    if ( aAt ) aNext[ pPin.name ] = aAt;
                } );
            }
            const aCovered = new Set( Array.from( figures.current?.querySelectorAll( 'img[data-mini]:not([data-pinned])' ) ?? [] )
                .filter( ( pImage ) => Object.values( aNext ).some( ( pAt ) => meets( pAt,
                    { left: pImage.offsetLeft, top: pImage.offsetTop, width: pImage.offsetWidth, height: pImage.offsetHeight } ) ) )
                .map( ( pImage ) => pImage.dataset.mini ) );
            setPinned( ( pWas ) => ( JSON.stringify( pWas ) === JSON.stringify( aNext ) ? pWas : aNext ) );
            setCovered( ( pWas ) => ( [ ...pWas ].join() === [ ...aCovered ].join() ? pWas : aCovered ) );
        };
        measure();
        window.addEventListener( 'resize', measure );
        const aObserver = typeof ResizeObserver === 'function' ? new ResizeObserver( measure ) : null;
        aObserver?.observe( aRoot );
        const aColumn = aRoot.querySelector( beside );
        if ( aColumn ) aObserver?.observe( aColumn );
        return () => {
            window.removeEventListener( 'resize', measure );
            aObserver?.disconnect();
        };
        // exclude is a fresh array each render; the seed and the column are what move the pins.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ aCount, beside, seed ] );

    // The page's pointer decides which edge button, if any, takes the pointer; its click opens a
    // mini a tap reached without a hover. Re-bound when the layers come and go with the count.
    useEffect( () => {
        const aRoot = figures.current?.parentElement;
        if ( !aRoot ) return undefined;
        let aPoint = null;
        let aFrame = 0;
        const under = ( pX, pY ) => Array.from( targets.current?.children ?? [] ).find( ( pButton ) => {
            const aBox = pButton.getBoundingClientRect();
            return pX >= aBox.left && pX < aBox.right && pY >= aBox.top && pY < aBox.bottom;
        } );
        const judge = () => {
            aFrame = 0;
            const aButton = aPoint && under( aPoint.x, aPoint.y );
            setLive( aButton && isClear( aRoot, aPoint.x, aPoint.y ) ? aButton.dataset.mini : null );
        };
        const later = () => { if ( !aFrame ) aFrame = window.requestAnimationFrame( judge ); };
        const move = ( pEvent ) => { aPoint = { x: pEvent.clientX, y: pEvent.clientY }; later(); };
        const leave = () => { aPoint = null; later(); };
        const tap = ( pEvent ) => {
            if ( pEvent.defaultPrevented || targets.current?.contains( pEvent.target ) ) return;
            const aButton = under( pEvent.clientX, pEvent.clientY );
            if ( aButton && isClear( aRoot, pEvent.clientX, pEvent.clientY ) ) setViewing( aButton.dataset.mini );
        };
        aRoot.addEventListener( 'pointermove', move, { passive: true } );
        aRoot.addEventListener( 'pointerleave', leave );
        aRoot.addEventListener( 'click', tap );
        window.addEventListener( 'scroll', later, { capture: true, passive: true } );
        return () => {
            aRoot.removeEventListener( 'pointermove', move );
            aRoot.removeEventListener( 'pointerleave', leave );
            aRoot.removeEventListener( 'click', tap );
            window.removeEventListener( 'scroll', later, { capture: true } );
            window.cancelAnimationFrame( aFrame );
        };
    }, [ aCount ] );

    if ( aCount === 0 ) return null;

    const aPicks = pickMinis( seed, aCount, { exclude, start, end } ).filter( ( pPick ) => !failed.has( pPick.name ) );
    const aPins = beside ? pinMinis( seed, { exclude } )
        .filter( ( pPin ) => pinned[ pPin.name ] && !failed.has( pPin.name ) )
        .map( ( pPin ) => ( { ...pPin, at: pinned[ pPin.name ] } ) ) : [];
    const pinStyle = ( pPin ) => ( { left: pPin.at.left + 'px', top: pPin.at.top + 'px', width: pPin.at.width + 'px',
        height: pPin.at.height + 'px', ...( pPin.flip ? { transform: 'scaleX(-1)' } : {} ) } );
    const open = ( pEvent, pName ) => {
        if ( isClear( figures.current?.parentElement, pEvent.clientX, pEvent.clientY ) ) setViewing( pName );
        else setLive( null );
    };
    return (
        <>
            <div aria-hidden="true" data-es-minis="" data-testid={ testId } ref={ figures }
                 className="pointer-events-none absolute inset-0 z-[-1] overflow-hidden print:hidden">
                { aPicks.map( ( pPick ) => (
                    <img key={ pPick.name }
                         src={ miniUrl( pPick.name ) }
                         alt=""
                         loading="lazy"
                         decoding="async"
                         draggable={ false }
                         width={ pPick.width }
                         height={ pPick.height }
                         data-testid={ testId + '-' + pPick.name }
                         data-mini={ pPick.name }
                         data-side={ pPick.side }
                         className={ 'absolute block select-none ' + ( pPick.side === 'center' ? CENTER : EDGE )
                             + ( covered.has( pPick.name ) ? ' invisible' : '' ) }
                         style={ placement( pPick ) }
                         onError={ () => setFailed( ( pSet ) => new Set( pSet ).add( pPick.name ) ) } />
                ) ) }
                { aPins.map( ( pPin ) => (
                    <img key={ pPin.name }
                         src={ miniUrl( pPin.name ) }
                         alt=""
                         loading="lazy"
                         decoding="async"
                         draggable={ false }
                         width={ pPin.at.width }
                         height={ pPin.at.height }
                         data-testid={ testId + '-' + pPin.name }
                         data-mini={ pPin.name }
                         data-side={ pPin.side }
                         data-pinned=""
                         className={ 'absolute block select-none ' + EDGE }
                         style={ pinStyle( pPin ) }
                         onError={ () => setFailed( ( pSet ) => new Set( pSet ).add( pPin.name ) ) } />
                ) ) }
            </div>
            <div aria-hidden="true" data-es-minis="" data-testid={ testId + '-targets' } ref={ targets }
                 className="pointer-events-none absolute inset-0 z-20 overflow-hidden print:hidden">
                { aPins.map( ( pPin ) => (
                    <button key={ pPin.name }
                            type="button"
                            tabIndex={ -1 }
                            aria-label={ miniTitle( pPin.name ) }
                            data-mini={ pPin.name }
                            data-pinned=""
                            data-testid={ testId + '-' + pPin.name + '-open' }
                            className={ 'absolute border-0 bg-transparent p-0 '
                                + ( live === pPin.name ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none' ) }
                            style={ pinStyle( pPin ) }
                            onClick={ ( pEvent ) => open( pEvent, pPin.name ) } />
                ) ) }
                { aPicks.filter( ( pPick ) => pPick.side !== 'center' && !covered.has( pPick.name ) ).map( ( pPick ) => (
                    <button key={ pPick.name }
                            type="button"
                            tabIndex={ -1 }
                            aria-label={ miniTitle( pPick.name ) }
                            data-mini={ pPick.name }
                            data-testid={ testId + '-' + pPick.name + '-open' }
                            className={ 'absolute border-0 bg-transparent p-0 '
                                + ( live === pPick.name ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none' ) }
                            style={ placement( pPick ) }
                            onClick={ ( pEvent ) => open( pEvent, pPick.name ) } />
                ) ) }
            </div>
            { viewing && <EsMiniView name={ viewing } onName={ setViewing } onClose={ () => setViewing( null ) }
                                     data-testid={ testId + '-view' } /> }
        </>
    );
}

/**
 * One mini, whole and at full strength, in a popup: Previous and Next under it, through every
 * render in MINI_NAMES, and MZ4250's Thingiverse page under those. The arrow keys step too.
 * Previous and Next are `<id>-previous` and `<id>-next`, the picture `<id>-image`, the link
 * `<id>-thingiverse`.
 */
export function EsMiniView( { name, onName, onClose, 'data-testid': testId = 'es-mini-view' } ) {
    const t = useEsStrings();
    const step = useCallback( ( pStep ) => onName( miniStep( name, pStep ) ), [ name, onName ] );

    useEffect( () => {
        const aKey = ( pEvent ) => {
            if ( pEvent.key === 'ArrowLeft' ) step( -1 );
            else if ( pEvent.key === 'ArrowRight' ) step( 1 );
        };
        document.addEventListener( 'keydown', aKey );
        return () => document.removeEventListener( 'keydown', aKey );
    }, [ step ] );

    return (
        <EsDialog open
                  onOpenChange={ ( pOpen ) => { if ( !pOpen ) onClose(); } }
                  title={ miniTitle( name ) }
                  data-testid={ testId }>
            <div className="flex flex-col items-center gap-3">
                <img src={ miniUrl( name ) }
                     alt={ miniTitle( name ) }
                     draggable={ false }
                     width={ 260 }
                     height={ Math.round( 260 * ASPECT ) }
                     data-testid={ testId + '-image' }
                     className="h-auto max-h-[60vh] w-auto max-w-full select-none" />
                <div className="my-3 flex items-center gap-2">
                    <Button severity="secondary" variant="outlined" size="small"
                            data-testid={ testId + '-previous' }
                            onClick={ () => step( -1 ) }>
                        <i className="pi pi-chevron-left" aria-hidden="true" />
                        { t( 'common-minis-previous', 'Previous' ) }
                    </Button>
                    <Button severity="secondary" variant="outlined" size="small"
                            data-testid={ testId + '-next' }
                            onClick={ () => step( 1 ) }>
                        { t( 'common-minis-next', 'Next' ) }
                        <i className="pi pi-chevron-right" aria-hidden="true" />
                    </Button>
                </div>
                <a href={ miniThing( name ) } target="_blank" rel="noreferrer"
                   data-testid={ testId + '-thingiverse' }
                   className="text-sm underline decoration-dotted hover:text-accent-hover">
                    { t( 'common-minis-thingiverse', 'MZ4250 on Thingiverse' ) }
                </a>
                <p className="-mt-1 mb-6 text-xs opacity-70" data-testid={ testId + '-license' }>
                    { t( 'common-minis-license', 'Used under license' ) }
                </p>
            </div>
        </EsDialog>
    );
}
